'use client'

import { useTranslations } from 'next-intl'
import { CaretLeftIcon } from '@phosphor-icons/react/dist/ssr/CaretLeft'
import { DotsThreeVerticalIcon } from '@phosphor-icons/react/dist/ssr/DotsThreeVertical'
import { UserCircleDashedIcon } from '@phosphor-icons/react/dist/ssr/UserCircleDashed'
import { Badge } from '@skop-app/lib-front'

import { Link } from '@/navigation'
import { Notifications, UserMenu } from '@/components/molecules'
import { DocLink } from '@/components/atoms'
import { useCurrentNavItem } from '@/hooks/useCurrentNavItem'
import { useUserContext } from '@/context'
import { resolveProjectPermissions } from '@/features/project/project.permissions'
import { useProject } from '@/services/queries'

import { ReviewsNotifications } from './Reviews/Notifications'

export function MainHeader({
  toggleShowNavbarAction,
}: {
  readonly toggleShowNavbarAction: () => void
}) {
  const { currentNavItem, id } = useCurrentNavItem()
  const t = useTranslations()
  const { ownership, authorization, organizationScope } = useUserContext()
  const { data: project } = useProject(id)

  const navKey = currentNavItem?.navBarTitleKey ?? currentNavItem?.key
  const translatedNavKey = navKey ? t(`Navigation.${navKey}`) : navKey
  const pageName = project?.name ?? translatedNavKey ?? ''
  const projectPermissions = resolveProjectPermissions({
    authorization,
    ownership,
    activeOrganizationId: organizationScope.activeOrganizationId,
    project,
  })
  const isProjectGuest =
    Boolean(project?.id) &&
    (projectPermissions.isGuestAccount || projectPermissions.isExternalProject)

  const { parent, backButton } = currentNavItem ?? {}

  const backButtonPath = backButton
    ? backButton.replace('[id]', id ?? '')
    : null

  return (
    <nav className="sticky top-0 z-20 flex h-[var(--app-header-height)] w-screen shrink-0 items-center justify-between gap-2 border-b border-zinc-200 bg-white px-[var(--app-shell-x)] text-slate-300 md:w-full">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {!!parent && !!backButton && (
          <Link
            className="group grid size-8 shrink-0 cursor-pointer place-content-center rounded-full text-sm font-medium hover:bg-gray-100"
            title="Retour"
            href={backButtonPath ?? parent?.path}
          >
            <CaretLeftIcon className="size-5 text-slate-100 group-hover:text-slate-500" />
          </Link>
        )}
        <p className="truncate cursor-default font-body text-base font-medium text-slate-700 md:text-lg">
          {pageName}
        </p>
        {isProjectGuest && (
          <Badge className="ml-1 flex shrink-0 gap-1 !pl-2" variant="secondary">
            <UserCircleDashedIcon className="size-4" />
            {t('Team.GUEST')}
          </Badge>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
        <DocLink component="topbar" />
        <ReviewsNotifications component="topbar" />
        <Notifications component="topbar" />
        <UserMenu />
        <button
          type="button"
          aria-label={t('Navigation.toggleNavbar')}
          className="p-2 md:hidden"
          onClick={toggleShowNavbarAction}
        >
          <DotsThreeVerticalIcon className="size-6" />
        </button>
      </div>
    </nav>
  )
}
